/**
 * Teacher Detail Page
 * View and manage individual teacher (admin only)
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Input } from '@/components/common/Input';
import { apiRequest } from '@/api/client';
import { useNotification } from '@/hooks/useNotification';

interface Teacher {
  teacherId: string;
  email: string;
  firstName: string;
  lastName: string;
  schoolId: string;
  schoolName: string;
  gradeLevel?: string;
  classLimit: number;
  classesCreated: number;
  isAdmin: boolean;
  isActive: boolean;
  verificationStatus: string;
  createdAt: string;
}

export const TeacherDetailPage = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newClassLimit, setNewClassLimit] = useState('');

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }
    if (teacherId) {
      fetchTeacher();
    }
  }, [teacherId, user, navigate]);

  const fetchTeacher = async () => {
    try {
      setIsLoading(true);
      
      // Get all teachers and find this one
      const response = await apiRequest<{ teachers: Teacher[] }>('get_school_teachers', {
        adminEmail: user?.email,
      });
      
      if (response.success && (response as any).teachers) {
        const foundTeacher = (response as any).teachers.find((t: Teacher) => t.teacherId === teacherId);
        if (foundTeacher) {
          setTeacher(foundTeacher);
          setNewClassLimit(foundTeacher.classLimit.toString());
        }
      }
    } catch (error) {
      console.error('Failed to fetch teacher:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateClassLimit = async () => {
    if (!teacher) return;
    
    const limit = parseInt(newClassLimit);
    if (isNaN(limit) || limit < 0) {
      showNotification('error', 'Please enter a valid class limit');
      return;
    }

    try {
      setIsUpdating(true);
      
      const response = await apiRequest('update_teacher_class_limit', {
        adminEmail: user?.email,
        teacherEmail: teacher.email,
        classLimit: limit,
      });

      if (response.success) {
        showNotification('success', 'Class limit updated successfully');
        fetchTeacher();
      } else {
        showNotification('error', 'Failed to update class limit');
      }
    } catch (error) {
      console.error('Failed to update class limit:', error);
      showNotification('error', 'Failed to update class limit');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMakeAdmin = async () => {
    if (!teacher) return;
    
    try {
      setIsUpdating(true);
      
      const response = await apiRequest('make_teacher_admin', {
        adminEmail: user?.email,
        teacherEmail: teacher.email,
      });

      if (response.success) {
        showNotification('success', `${teacher.firstName} ${teacher.lastName} is now an administrator`);
        fetchTeacher();
      } else {
        showNotification('error', 'Failed to make teacher admin');
      }
    } catch (error) {
      console.error('Failed to make teacher admin:', error);
      showNotification('error', 'Failed to make teacher admin');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveAdmin = async () => {
    if (!teacher) return;
    
    try {
      setIsUpdating(true);
      
      const response = await apiRequest('remove_teacher_admin', {
        adminEmail: user?.email,
        teacherEmail: teacher.email,
      });

      if (response.success) {
        showNotification('success', `${teacher.firstName} ${teacher.lastName} is no longer an administrator`);
        fetchTeacher();
      } else {
        showNotification('error', 'Failed to remove admin privileges');
      }
    } catch (error) {
      console.error('Failed to remove admin privileges:', error);
      showNotification('error', 'Failed to remove admin privileges');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="p-12 text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Teacher not found
            </h3>
            <p className="text-slate-600 mb-6">
              This teacher may have been deleted or you don't have permission to view them.
            </p>
            <Button variant="primary" onClick={() => navigate('/teachers')}>
              Back to Teachers
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleDeactivateTeacher = async () => {
    try {
      setIsUpdating(true);
      
      const response = await apiRequest('delete_teacher', {
        adminEmail: user?.email,
        teacherEmail: teacher.email,
      });

      if (response.success) {
        showNotification('success', 'Teacher deactivated successfully');
        navigate('/teachers');
      } else {
        showNotification('error', response.error || 'Failed to deactivate teacher');
      }
    } catch (error) {
      console.error('Failed to deactivate teacher:', error);
      showNotification('error', 'Failed to deactivate teacher');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900">
              {teacher.firstName} {teacher.lastName}
            </h1>
            {teacher.isAdmin && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-lg">
                Administrator
              </span>
            )}
          </div>
          <p className="text-slate-600 mt-2">{teacher.email}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/teachers')}>
          ← Back to Teachers
        </Button>
      </div>

      {/* Teacher Info */}
      <Card title="Teacher Information">
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-600">Email</label>
              <p className="text-lg text-slate-900">{teacher.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">School</label>
              <p className="text-lg text-slate-900">{teacher.schoolName}</p>
            </div>
            {teacher.gradeLevel && (
              <div>
                <label className="text-sm font-medium text-slate-600">Grade Level</label>
                <p className="text-lg text-slate-900">Year {teacher.gradeLevel}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-600">Status</label>
              <p className="text-lg text-slate-900">
                {teacher.isActive ? (
                  <span className="text-emerald-600">Active</span>
                ) : (
                  <span className="text-slate-500">Inactive</span>
                )}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Classes</label>
              <p className="text-lg text-slate-900">
                {teacher.classesCreated} / {teacher.classLimit}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Joined</label>
              <p className="text-lg text-slate-900">
                {new Date(teacher.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Class Limit */}
      <Card title="Class Limit">
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            Set the maximum number of classes this teacher can create
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Input
                type="number"
                label="Class Limit"
                value={newClassLimit}
                onChange={(e) => setNewClassLimit(e.target.value)}
                min="0"
                max="50"
              />
            </div>
            <Button
              variant="primary"
              onClick={handleUpdateClassLimit}
              disabled={isUpdating || newClassLimit === teacher.classLimit.toString()}
            >
              Update Limit
            </Button>
          </div>
        </div>
      </Card>

      {/* Admin Privileges */}
      <Card title="Administrator Privileges">
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            Administrators can manage all teachers, classes, and students in the school
          </p>
          {teacher.isAdmin ? (
            <Button
              variant="danger"
              onClick={handleRemoveAdmin}
              disabled={isUpdating}
            >
              Remove Admin Privileges
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleMakeAdmin}
              disabled={isUpdating}
            >
              Make Administrator
            </Button>
          )}
        </div>
      </Card>

      {/* Danger Zone */}
      <Card title="Danger Zone">
        <div className="p-6 space-y-3">
          <Button
            variant="danger"
            onClick={handleDeactivateTeacher}
            className="w-full"
          >
            Deactivate Teacher
          </Button>
        </div>
      </Card>
    </div>
  );
};
